import { useState, useEffect } from 'react';
import {
  Modal, ModalDialog, ModalClose, DialogTitle, DialogContent,
  List, ListItem, ListItemButton, ListItemContent, ListItemDecorator,
  Typography, Chip, CircularProgress, Box, Input,
} from '@mui/joy';
import { Person, Search } from '@mui/icons-material';
import { User } from '@/types/user';
import { TaskWithAssignees } from '@/types/project';
import { userService } from '@/services/userService';

interface TaskAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  task: TaskWithAssignees;
  onAssign: (employeeId: number) => Promise<void>;
  onUnassign: (employeeId: number) => Promise<void>;
}

export default function TaskAssignmentModal({ open, onClose, task, onAssign, onUnassign }: TaskAssignmentModalProps) {
  const [employees, setEmployees] = useState<User[]>([]);
  const [qualifiedIds, setQualifiedIds] = useState<Set<number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    const load = async () => {
      setLoading(true);
      try {
        const allUsers = await userService.getEmployees();
        setEmployees(allUsers.filter(u => u.is_active));

        if (task.shift_type_id) {
          const batchTypes = await userService.getUsersShiftTypes(allUsers.map(u => u.id));
          const qualified = new Set<number>();
          for (const [uid, types] of Object.entries(batchTypes)) {
            if (types.some(t => t.id === task.shift_type_id)) {
              qualified.add(Number(uid));
            }
          }
          setQualifiedIds(qualified);
        } else {
          setQualifiedIds(null);
        }
      } catch {
        // fallback
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, task.shift_type_id]);

  const isAssigned = (id: number) => task.assignees.some(a => a.id === id);
  const isQualified = (id: number) => qualifiedIds === null || qualifiedIds.has(id);

  const handleToggle = async (id: number) => {
    setActionLoading(id);
    try {
      if (isAssigned(id)) {
        await onUnassign(id);
      } else {
        await onAssign(id);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return (
      e.first_name.toLowerCase().includes(q) ||
      e.last_name.toLowerCase().includes(q) ||
      e.email.toLowerCase().includes(q)
    );
  });

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 460, width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
        <ModalClose />
        <DialogTitle>Assign Employees</DialogTitle>
        <DialogContent>
          {task.shift_type && (
            <Typography level="body-sm" sx={{ mb: 1 }}>
              Requires qualification: <Chip size="sm" variant="soft" sx={{ bgcolor: task.shift_type.color + '22', color: task.shift_type.color }}>{task.shift_type.name}</Chip>
            </Typography>
          )}
          <Input
            placeholder="Search employees..."
            startDecorator={<Search />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <List>
              {filtered.map(emp => {
                const assigned = isAssigned(emp.id);
                const qualified = isQualified(emp.id);
                return (
                  <ListItem key={emp.id}>
                    <ListItemButton
                      disabled={!qualified || actionLoading === emp.id}
                      onClick={() => handleToggle(emp.id)}
                      selected={assigned}
                      color={assigned ? 'primary' : undefined}
                    >
                      <ListItemDecorator><Person /></ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm">
                          {emp.first_name} {emp.last_name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">{emp.email}</Typography>
                      </ListItemContent>
                      {assigned && <Chip size="sm" color="primary" variant="soft">Assigned</Chip>}
                      {!qualified && <Chip size="sm" color="danger" variant="outlined">Not Qualified</Chip>}
                      {actionLoading === emp.id && <CircularProgress size="sm" />}
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {filtered.length === 0 && (
                <Typography level="body-sm" sx={{ py: 2, textAlign: 'center' }}>No employees found</Typography>
              )}
            </List>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
